import { computed, inject, Injectable, signal } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    ContentData,
    ContentGroupData,
    GroupDetailData,
    MarketTypeGroupData,
    RuleRangeTypeData,
    EntityGroupData,
} from '../models/group-management.model';
import { GroupApiCallService } from './group-api-call.service';
import { meaningfulTextValidator } from '../../../shared/validators/meaningful-text.validator';
import { Observable, of } from 'rxjs';
import {
    ContentGroupService,
    MarketTypeGroupService,
    RuleRangeTypeGroupService,
    EntityGroupService,
} from './modules';
import { DetailAttribute } from '../../../shared/models/shared.model';

@Injectable({ providedIn: 'root' })
export class GroupDetailService {
    private groupApiCallService = inject(GroupApiCallService);
    private entityGroupService = inject(EntityGroupService);
    private contentGroupService = inject(ContentGroupService);
    private marketTypeGroupService = inject(MarketTypeGroupService);
    private ruleRangeGroupService = inject(RuleRangeTypeGroupService);

    private groupColors = [
        { id: 1, name: 'Red', parentId: 0, colorCode: '#f44336' },
        { id: 2, name: 'Blue', parentId: 0, colorCode: '#154eae' },
        { id: 3, name: 'Green', parentId: 0, colorCode: '#75c926' },
        { id: 4, name: 'Orange', parentId: 0, colorCode: '#FF6600' },
    ];

    private currentService = computed(() => {
        switch (this.currentGroup()) {
            case 'EntityGroups':
                return this.entityGroupService;
            case 'ContentGroups':
                return this.contentGroupService;
            case 'MarketTypeGroups':
                return this.marketTypeGroupService;
            case 'RuleRangeGroups':
                return this.ruleRangeGroupService;
            default:
                throw new Error('No service found for the current group');
        }
    });

    groupDetailAttributes = signal<DetailAttribute[]>([]);
    currentDetailData = signal<
        | EntityGroupData
        | ContentGroupData
        | MarketTypeGroupData
        | RuleRangeTypeData
    >(
        {} as
            | EntityGroupData
            | ContentGroupData
            | MarketTypeGroupData
            | RuleRangeTypeData,
    );
    initialDetailData = signal<
        | EntityGroupData
        | ContentGroupData
        | MarketTypeGroupData
        | RuleRangeTypeData
    >(
        {} as
            | EntityGroupData
            | ContentGroupData
            | MarketTypeGroupData
            | RuleRangeTypeData,
    );
    currentGroup = signal<string>('');
    detailLoadStatus = signal<'ready' | 'loading' | 'error'>('ready');

    private pendingDetailId: number | null = null;

    private resetCurrentDetailData() {
        const emptyData = {} as
            | EntityGroupData
            | ContentGroupData
            | MarketTypeGroupData
            | RuleRangeTypeData;
        this.currentDetailData.set(emptyData);
        this.initialDetailData.set(emptyData);
    }

    getCurrentGroup() {
        return this.currentGroup;
    }

    getGroupDetailAttributes() {
        return this.groupDetailAttributes;
    }

    getGroupDetailData() {
        return this.currentDetailData;
    }

    getInitialGroupDetailData() {
        return this.initialDetailData;
    }

    getCurrentId() {
        return this.currentDetailData().id;
    }

    getDetailLoadStatus() {
        return this.detailLoadStatus;
    }

    loadDetailData(id: number): void {
        this.pendingDetailId = id;
        this.detailLoadStatus.set('loading');
        this.groupApiCallService
            .getGroupDetailData(this.currentGroup(), id)
            .subscribe({
                next: (data) => {
                    // AB#8975: a newer navigation may have already requested
                    // a different id while this request was in flight — a
                    // stale response must not overwrite the newer record's
                    // data. pendingDetailId always reflects the most recent
                    // request, so a mismatch means this response is stale.
                    if (id !== this.pendingDetailId) {
                        return;
                    }
                    if (this.currentGroup() === 'RuleRangeGroups') {
                        data = this.normalizeRuleRangeData(data);
                    }
                    if (
                        this.currentGroup() === 'EntityGroups' &&
                        data.hasOwnProperty('colorCode')
                    ) {
                        data['colorCode'] = this.getColorIdByCode(
                            data['colorCode'],
                        );
                    }
                    this.initialDetailData.set(data);
                    this.currentDetailData.set(data);
                    this.syncChosenTenant(data?.tenantId);
                    this.detailLoadStatus.set('ready');
                },
                error: (error) => {
                    if (id !== this.pendingDetailId) {
                        return;
                    }
                    console.error('Error loading group detail data:', error);
                    this.detailLoadStatus.set('error');
                },
            });
    }

    retryLoadDetailData(): void {
        if (this.pendingDetailId === null) {
            return;
        }
        this.loadDetailData(this.pendingDetailId);
    }

    /**
     * The Rule Range Group GET endpoint returns its band rows under a
     * different key than the one the editor binds to (`multipliers`).
     * Normalise the response so every stored Min/Max/Multiplier row is
     * hydrated into the editable table.
     */
    private normalizeRuleRangeData(data: any): any {
        const normalized = { ...data };
        const rows =
            normalized['multipliers'] ??
            normalized['rangeRows'] ??
            normalized['ranges'];
        delete normalized['rangeRows'];
        delete normalized['ranges'];
        normalized['multipliers'] = Array.isArray(rows)
            ? rows.map((row: any) => ({
                  minValue: null,
                  maxValue: null,
                  multiplier: null,
                  ...row,
              }))
            : [];
        return normalized;
    }

    addGroupDetailAttributes(attributes: DetailAttribute[]) {
        if (!attributes?.length) {
            return;
        }

        this.groupDetailAttributes.update((current) => [
            ...current,
            ...attributes,
        ]);
    }

    updateGroupDetailData(event: Record<string, any>): void {
        if (
            'tenantId' in event &&
            this.currentDetailData().tenantId !== event['tenantId']
        ) {
            this.syncChosenTenant(event['tenantId']);
        }
        this.currentDetailData.update((current) => {
            return { ...current, ...event };
        });
    }

    loadGroupDetailAttributes(groupType: string): void {
        this.currentGroup.set(groupType);
        this.resetCurrentDetailData();
        this.pendingDetailId = null;
        this.detailLoadStatus.set('ready');

        this.groupDetailAttributes.set([
            {
                label: 'Name',
                name: 'name',
                apiName: 'name',
                type: 'text',
                placeholder: 'Enter group name',
                control: [Validators.required],
            },
            {
                label: 'Description',
                name: 'description',
                apiName: 'description',
                type: 'text',
                // Defence-in-depth: meaningful description (AB#8669). This
                // validator also fails empty values, so it subsumes
                // Validators.required — do not stack both.
                placeholder: 'Give a short description of the group',
                control: [meaningfulTextValidator()],
            },
            {
                label: 'Tenants',
                name: 'tenants',
                apiName: 'tenantId',
                type: 'select',
                placeholder: 'Select tenant',
                control: [Validators.required],
            },
        ]);

        this.groupDetailAttributes.update((current) => [
            ...current,
            ...this.currentService().loadGroupAttributes(),
            {
                label: 'Is Absolute',
                name: 'isAbsolute',
                apiName: 'isAbsolute',
                type: 'checkbox',
                placeholder: '',
                control: [],
            },
        ]);
    }

    loadContentData(
        attributeName: string,
        parentContext?: { name: string; parentId: number },
    ): Observable<ContentData[]> {
        if (
            attributeName !== 'colors' &&
            attributeName !== 'rulerangetypes' &&
            attributeName !== 'ruletypes'
        )
            return this.groupApiCallService.getContentData(
                attributeName,
                parentContext,
            ) as Observable<ContentData[]>;
        if (attributeName === 'rulerangetypes')
            return this.ruleRangeGroupService.getRangeTypes(
                parentContext?.parentId || 1,
            ) as Observable<ContentData[]>;
        if (attributeName === 'ruletypes')
            return this.ruleRangeGroupService.getRuleTypes() as Observable<
                ContentData[]
            >;
        return of(this.getColors());
    }

    private getColors() {
        return this.groupColors;
    }

    private getColorIdByCode(code: string): number | null {
        return (
            this.groupColors.find((color) => color.colorCode === code)?.id ??
            null
        );
    }

    private getColorCodeById(id: number): string {
        return (
            this.groupColors.find((color) => color.id === id)?.colorCode ?? ''
        );
    }

    saveGroupData(): Observable<any> {
        const group = this.currentGroup();
        const id = this.getCurrentId();
        const detailData = this.currentDetailData();

        if (!group) {
            throw new Error('Current group is not set');
        }

        if (
            this.currentGroup() === 'EntityGroups' &&
            typeof (detailData as EntityGroupData).colorCode === 'number'
        ) {
            (detailData as EntityGroupData).colorCode = this.getColorCodeById(
                (detailData as EntityGroupData).colorCode as number,
            );
        }

        return id
            ? this.groupApiCallService.updateGroupData(group, id, detailData)
            : this.groupApiCallService.addGroupData(group, detailData);
    }

    private syncChosenTenant(tenantId?: number) {
        if (
            tenantId === undefined ||
            tenantId === null ||
            Number.isNaN(tenantId)
        ) {
            return;
        }

        this.groupApiCallService.setChosenTenant(String(tenantId));
    }
}
